import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { screenFromPathname } from '../data/flowIaRouteMap';
import { flowIaSuggestionPrefix, resolveFlowIaApiScreen } from '../data/flowIaScreenMeta';
import { getFlowIAScreenContext } from '../hooks/flowIAScreenContextStore';
import { getFlowIaStatus, postFlowIaChat } from '../services/flowIaService';
import { canWith } from '../utils/rbacPermissions';
import { FlowIAResponseRenderer } from './FlowIAResponseRenderer';

function pathTitleKey(path) {
  const keys = {
    '/': 'dashboard.title',
    '/employees': 'employees.title',
    '/settings': 'settings.heroTitle',
    '/notifications': 'notifications.title',
  };
  return keys[path] || 'flowIa.floating.contextGeneric';
}

/**
 * Asistente Flow IA global — botón flotante + panel lateral.
 */
export function FlowIAFloatingAssistant() {
  const { t, locale, permSummary, isProductionUi } = useApp();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [sources, setSources] = useState([]);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const canChat = canWith(permSummary, 'flow_ia', 'ai.use', isProductionUi);
  const canView = canWith(permSummary, 'flow_ia', 'view', isProductionUi);
  const enabled = canView || canChat;

  const uiScreen = useMemo(() => screenFromPathname(location.pathname), [location.pathname]);
  const apiScreen = useMemo(() => resolveFlowIaApiScreen(uiScreen), [uiScreen]);
  const prefix = useMemo(() => flowIaSuggestionPrefix(uiScreen), [uiScreen]);

  const screenLabel = useMemo(() => {
    const key = pathTitleKey(location.pathname);
    const val = t(key);
    return val === key ? t('flowIa.floating.contextGeneric') : val;
  }, [location.pathname, t]);

  const suggestions = useMemo(() => {
    return [1, 2, 3, 4].map((i) => {
      const key = `flowIa.${prefix}.q${i}`;
      const txt = t(key);
      return txt === key ? t(`flowIa.gen.q${i}`) : txt;
    });
  }, [t, prefix]);

  useEffect(() => {
    if (!canView) return;
    getFlowIaStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [canView]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const cancelRequest = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const q = (text || '').trim();
      if (!q || !canChat || loading) return;
      setError(null);
      setMessages((prev) => [...prev, { role: 'user', content: q }]);
      setInput('');
      setLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));
      const pageCtx = getFlowIAScreenContext();
      try {
        const data = await postFlowIaChat(
          {
            screen: apiScreen,
            question: q,
            locale,
            context: { ...pageCtx, title: screenLabel, route: location.pathname },
            history,
          },
          { signal: controller.signal }
        );
        setSources(Array.isArray(data.sources) ? data.sources : []);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: data.answer || t('flowIa.fallbackAnswer'),
            meta: data.source,
          },
        ]);
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setError(t('flowIa.errorGeneric'));
        setMessages((prev) => [...prev, { role: 'assistant', content: t('flowIa.fallbackAnswer'), meta: 'rules' }]);
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    },
    [apiScreen, canChat, loading, locale, location.pathname, messages, screenLabel, t]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const newConversation = () => {
    cancelRequest();
    setMessages([]);
    setSources([]);
    setError(null);
  };

  if (!enabled) {
    return (
      <button
        type="button"
        className="flow-ia-fab flow-ia-fab--disabled"
        disabled
        aria-label={t('flowIa.floating.fabDisabled')}
        title={t('flowIa.floating.fabDisabled')}
      >
        <span className="flow-ia-fab__icon" aria-hidden>
          ✦
        </span>
      </button>
    );
  }

  const fabState = loading ? 'loading' : error ? 'error' : 'ready';
  const modeLabel =
    status?.mode === 'active'
      ? t('flowIa.settingsPanel.stateActive')
      : status?.mode === 'pending_key'
        ? t('flowIa.settingsPanel.statePending')
        : status?.mode === 'disabled'
          ? t('flowIa.settingsPanel.stateDisabled')
          : t('flowIa.settingsPanel.stateFallback');

  return (
    <>
      <button
        type="button"
        className={`flow-ia-fab flow-ia-fab--${fabState}${open ? ' flow-ia-fab--open' : ''}`}
        aria-label={t('flowIa.floating.fabOpen')}
        title={t('flowIa.floating.fabOpen')}
        aria-expanded={open}
        aria-controls="flow-ia-panel"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flow-ia-fab__icon" aria-hidden>
          ✦
        </span>
      </button>

      {open ? (
        <div className="flow-ia-overlay" role="presentation" onClick={() => setOpen(false)} />
      ) : null}

      <aside
        id="flow-ia-panel"
        className={`flow-ia-panel${open ? ' flow-ia-panel--open' : ''}`}
        aria-hidden={!open}
        aria-label={t('flowIa.widgetTitle')}
      >
        <header className="flow-ia-panel__head">
          <div>
            <h2 className="flow-ia-panel__title">{t('flowIa.widgetTitle')}</h2>
            <p className="flow-ia-panel__context">
              {t('flowIa.floating.contextLabel')}: <strong>{screenLabel}</strong>
            </p>
            <p className="flow-ia-panel__mode text-sm panel__muted">{modeLabel}</p>
          </div>
          <div className="flow-ia-panel__head-actions">
            <button type="button" className="btn btn--ghost btn--sm" onClick={newConversation}>
              {t('flowIa.floating.newChat')}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)} aria-label={t('flowIa.floating.close')}>
              ×
            </button>
          </div>
        </header>

        <div className="flow-ia-panel__body" ref={listRef}>
          {messages.length === 0 ? (
            <div className="flow-ia-panel__empty">
              <p className="panel__muted text-sm">{t('flowIa.floating.intro')}</p>
              <div className="flow-ia-widget__chips">
                {suggestions.map((s) => (
                  <button key={s} type="button" className="flow-ia-chip" disabled={!canChat || loading} onClick={() => sendMessage(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ul className="flow-ia-panel__messages">
              {messages.map((m, i) => (
                <li key={`${i}-${m.role}`} className={`flow-ia-panel__msg flow-ia-panel__msg--${m.role}`}>
                  <FlowIAResponseRenderer text={m.content} />
                </li>
              ))}
              {loading ? <li className="flow-ia-panel__msg flow-ia-panel__msg--assistant panel__muted">{t('flowIa.loading')}</li> : null}
            </ul>
          )}
          {error ? (
            <p className="flow-ia-widget__err text-sm">
              {error}{' '}
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => sendMessage(messages[messages.length - 2]?.content || input)}>
                {t('flowIa.floating.retry')}
              </button>
            </p>
          ) : null}
          {sources.length > 0 ? (
            <p className="flow-ia-panel__sources text-sm panel__muted">
              {t('flowIa.floating.sources')}: {sources.join(' · ')}
            </p>
          ) : null}
          <p className="flow-ia-panel__disclaimer text-sm panel__muted">{t('flowIa.floating.noMutations')}</p>
        </div>

        <form className="flow-ia-panel__form" onSubmit={onSubmit}>
          <label className="sr-only" htmlFor="flow-ia-floating-input">
            {t('flowIa.inputLabel')}
          </label>
          <textarea
            id="flow-ia-floating-input"
            ref={inputRef}
            className="flow-ia-panel__input"
            rows={2}
            placeholder={canChat ? t('flowIa.floating.inputPlaceholder') : t('flowIa.floating.noPermission')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onInputKeyDown}
            disabled={!canChat || loading}
          />
          <div className="flow-ia-panel__form-actions">
            {loading ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={cancelRequest}>
                {t('flowIa.floating.cancel')}
              </button>
            ) : null}
            <button type="submit" className="btn btn--secondary btn--sm" disabled={!canChat || loading || !input.trim()}>
              {t('flowIa.ask')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
